import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { FaSave, FaAngleLeft } from 'react-icons/fa';
import NoAccess from '../../../Routes/noAccess';
import api, { NOT_FOUND_MESSAGE } from '../../services/miscellaneousReceiptRAApi';
import { fmt3, num } from '../leaseGeneralInvoiceReceipt/allocationHelpers';
import PaymentPanel from './PaymentPanel';
import AllocationPanel from './AllocationPanel';
import LinesGrid from './LinesGrid';
import {
    DEFAULT_TRANSACTION_TYPE, allocatedTotal, balance, buildCreateRequest, fieldLabels,
    validateAllocation, validatePayment, validateSave,
} from './miscReceiptHelpers';
import { LEAVE_MESSAGE, MISC_RECEIPT_BASE, readPermission } from './miscReceiptConstants';

const DROPDOWN_TYPES = ['Properties', 'Tenants', 'PaymentTypes', 'TransactionTypes', 'DrawnOnBanks'];
const DUPLICATE_WARNING = 'Misc receipt is already created against this Lease No.';

const initialPayment = () => ({
    totalAmount: '',
    receiptDate: new Date(),
    transactionType: '',
    debitAccountCode: '',
    chequeNo: '',
    drawnOnBank: '',
    chequeDate: new Date(),
    refNo: '',
    narration: '',
});

const emptyAlloc = (showAll = false) => ({
    propertyNo: '', unitNo: '', leaseNo: '', tenantNo: '', paymentTypeDesc: '', amount: '', showAll,
});

let lineKey = 0;

const MiscellaneousReceiptRAForm = () => {
    const navigate = useNavigate();
    const [permissions] = useState(() => readPermission());

    const [dropdowns, setDropdowns] = useState({ properties: [], tenants: [], paymentTypes: [], transactionTypes: [], drawnOnBanks: [] });
    const [debitAccounts, setDebitAccounts] = useState([]);
    const [units, setUnits] = useState([]);
    const [leases, setLeases] = useState([]);
    const [details, setDetails] = useState(null);
    const [payment, setPayment] = useState(initialPayment);
    const [alloc, setAlloc] = useState(() => emptyAlloc());
    const [lines, setLines] = useState([]);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const [warning, setWarning] = useState('');
    const [lookupLoading, setLookupLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Latest values for async handlers, which would otherwise read stale closures.
    const allocRef = useRef(alloc);
    allocRef.current = alloc;
    const paymentRef = useRef(payment);
    paymentRef.current = payment;
    const requests = useRef({ debit: 0, lookup: 0, details: 0, duplicate: 0 });

    const total = num(payment.totalAmount);
    const unlocked = total > 0;
    const remaining = useMemo(() => balance(total, lines), [total, lines]);
    const allocated = useMemo(() => allocatedTotal(lines), [lines]);
    const canSave = unlocked && lines.length > 0 && remaining === 0 && !saving;

    /* ---------------- loaders ---------------- */

    const loadDebitAccounts = useCallback(async (transactionType, propertyNo) => {
        const id = ++requests.current.debit;
        if (!transactionType) return;
        const res = await api.getDebitAccounts({ transactionType, propertyNo });
        if (id !== requests.current.debit) return;
        const list = res.ok ? res.result || [] : [];
        if (!res.ok) toast.error(res.message);
        setDebitAccounts(list);
        // The first item is the default account for that method and property.
        setPayment((p) => ({ ...p, debitAccountCode: list[0]?.accountCode || '' }));
    }, []);

    const loadUnitsAndLeases = useCallback(async (propertyNo, showAll) => {
        const id = ++requests.current.lookup;
        if (!propertyNo) { setUnits([]); setLeases([]); return; }
        setLookupLoading(true);
        const [unitRes, leaseRes] = await Promise.all([api.getUnits({ propertyNo, showAll }), api.getLeases({ propertyNo, showAll })]);
        if (id !== requests.current.lookup) return;
        setLookupLoading(false);
        if (!unitRes.ok) toast.error(unitRes.message);
        if (!leaseRes.ok) toast.error(leaseRes.message);
        setUnits(unitRes.ok ? unitRes.result || [] : []);
        setLeases(leaseRes.ok ? leaseRes.result || [] : []);
    }, []);

    // Dropdowns once on open (the api module also caches them for the session).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await api.getDropdowns(DROPDOWN_TYPES);
            if (cancelled) return;
            if (!res.ok) { toast.error(res.message); return; }
            const d = res.dropdowns || {};
            const next = {
                properties: d.Properties || [],
                tenants: d.Tenants || [],
                paymentTypes: d.PaymentTypes || [],
                transactionTypes: d.TransactionTypes || [],
                drawnOnBanks: d.DrawnOnBanks || [],
            };
            setDropdowns(next);
            const initial = next.transactionTypes.find((t) => t.text === DEFAULT_TRANSACTION_TYPE) || next.transactionTypes[0];
            if (initial) {
                setPayment((p) => ({ ...p, transactionType: initial.text }));
                loadDebitAccounts(initial.text, '');
            }
        })();
        return () => { cancelled = true; };
    }, [loadDebitAccounts]);

    /* ---------------- leaving the page ---------------- */

    const hasUnsaved = lines.length > 0 && !saving;
    useEffect(() => {
        if (!hasUnsaved) return undefined;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        // In-app links (the sidebar) never trigger beforeunload, so intercept them in the capture phase.
        const onClick = (e) => {
            const anchor = e.target.closest?.('a[href]');
            if (!anchor || anchor.target === '_blank' || !anchor.getAttribute('href').startsWith('/')) return;
            if (!window.confirm(LEAVE_MESSAGE)) { e.preventDefault(); e.stopPropagation(); }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onClick, true);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onClick, true);
        };
    }, [hasUnsaved]);

    /* ---------------- payment panel ---------------- */

    const handlePaymentChange = (field, value) => {
        setPayment((p) => ({ ...p, [field]: value }));
        setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
        if (serverError) setServerError('');
    };

    const handleTransactionTypeChange = (transactionType) => {
        setPayment((p) => {
            const before = fieldLabels(p.transactionType);
            const after = fieldLabels(transactionType);
            const next = { ...p, transactionType };
            // A hidden or relabelled field must not carry the old meaning (card type vs cheque no).
            ['chequeNo', 'drawnOnBank'].forEach((f) => {
                if (!after[f].visible || after[f].label !== before[f].label) next[f] = '';
            });
            if (!after.chequeDate.visible || after.chequeDate.label !== before.chequeDate.label) next.chequeDate = after.chequeDate.visible ? new Date() : null;
            return next;
        });
        setErrors({});
        loadDebitAccounts(transactionType, allocRef.current.propertyNo);
    };

    /* ---------------- allocation panel ---------------- */

    /** Fills the read-only labels and the selection from a lease. */
    const loadLeaseDetails = async (leaseNo) => {
        const id = ++requests.current.details;
        const res = await api.getTenantDetails({ leaseNo });
        if (id !== requests.current.details) return;
        if (!res.ok || res.result?.hasAccess === false) {
            toast.error(res.ok ? NOT_FOUND_MESSAGE : res.message);
            setDetails(null);
            return;
        }
        const d = res.result;
        setDetails(d);
        const previousProperty = allocRef.current.propertyNo;
        setAlloc((a) => ({
            ...a,
            leaseNo: d.leaseNo || leaseNo,
            tenantNo: d.tenantNo || a.tenantNo,
            propertyNo: d.propertyNo || a.propertyNo,
            unitNo: d.unitNo || a.unitNo,
        }));
        if (d.propertyNo && d.propertyNo !== previousProperty) {
            loadUnitsAndLeases(d.propertyNo, allocRef.current.showAll);
            loadDebitAccounts(paymentRef.current.transactionType, d.propertyNo);
        }
    };

    const clearSelection = () => {
        requests.current.details += 1;
        setDetails(null);
        setWarning('');
    };

    const handleProperty = (propertyNo) => {
        clearSelection();
        setAlloc((a) => ({ ...emptyAlloc(a.showAll), propertyNo, paymentTypeDesc: a.paymentTypeDesc, amount: a.amount }));
        loadUnitsAndLeases(propertyNo, allocRef.current.showAll);
        loadDebitAccounts(paymentRef.current.transactionType, propertyNo);
    };

    const handleUnit = (unit) => {
        clearSelection();
        setAlloc((a) => ({ ...a, unitNo: unit?.unitNo || '', leaseNo: unit?.leaseNo || '', tenantNo: '' }));
        if (unit?.leaseNo) loadLeaseDetails(unit.leaseNo);
    };

    const handleLease = (leaseNo) => {
        clearSelection();
        setAlloc((a) => ({ ...a, leaseNo, tenantNo: '', unitNo: leaseNo ? a.unitNo : '' }));
        if (leaseNo) loadLeaseDetails(leaseNo);
    };

    const handleTenant = async (tenantNo) => {
        setAlloc((a) => ({ ...a, tenantNo }));
        if (!tenantNo || allocRef.current.leaseNo) return;
        const id = ++requests.current.details;
        const res = await api.getTenantDetails({ tenantNo });
        if (id !== requests.current.details) return;
        if (res.ok && res.result?.hasAccess !== false) setDetails(res.result);
        else toast.error(res.ok ? NOT_FOUND_MESSAGE : res.message);
    };

    const handleShowAll = (showAll) => {
        setAlloc((a) => ({ ...a, showAll }));
        loadUnitsAndLeases(allocRef.current.propertyNo, showAll);
    };

    const handlePaymentType = async (paymentTypeDesc) => {
        setAlloc((a) => ({ ...a, paymentTypeDesc }));
        setWarning('');
        const leaseNo = allocRef.current.leaseNo;
        const id = ++requests.current.duplicate;
        if (!leaseNo || !paymentTypeDesc) return;
        const res = await api.checkDuplicate({ leaseNo, paymentTypeDesc });
        if (id !== requests.current.duplicate) return;
        // Warning only: the user may still add the line.
        if (res.ok && res.result === true) setWarning(DUPLICATE_WARNING);
    };

    const handleAdd = () => {
        const paymentType = dropdowns.paymentTypes.find((p) => p.text === alloc.paymentTypeDesc);
        const error = validateAllocation(
            { ...alloc, creditAccountCode: paymentType?.value, amount: alloc.amount },
            lines,
            remaining,
        );
        if (error) { toast.warning(error); return; }
        const property = dropdowns.properties.find((p) => p.value === alloc.propertyNo);
        const tenant = dropdowns.tenants.find((t) => t.value === alloc.tenantNo);
        lineKey += 1;
        setLines((prev) => [...prev, {
            key: lineKey,
            propertyNo: alloc.propertyNo,
            propertyName: property?.text || alloc.propertyNo,
            unitNo: alloc.unitNo,
            leaseNo: alloc.leaseNo,
            tenantNo: alloc.tenantNo,
            tenantName: tenant?.text || details?.tenantName || alloc.tenantNo,
            paymentTypeDesc: alloc.paymentTypeDesc,
            creditAccountCode: paymentType.value,
            amount: num(alloc.amount),
        }]);
        // Keep property, unit, lease and tenant so several payment types can be added quickly.
        setAlloc((a) => ({ ...a, paymentTypeDesc: '', amount: '' }));
        setWarning('');
        requests.current.duplicate += 1;
    };

    const handleRemove = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

    const handleReset = () => {
        if (lines.length > 0 && !window.confirm('Clear the allocated lines?')) return;
        requests.current.lookup += 1;
        clearSelection();
        setLines([]);
        setAlloc(emptyAlloc(allocRef.current.showAll));
        setUnits([]);
        setLeases([]);
    };

    /* ---------------- save / cancel ---------------- */

    const handleSave = async () => {
        const paymentErrors = validatePayment(payment);
        setErrors(paymentErrors);
        const firstError = Object.values(paymentErrors).find(Boolean);
        if (firstError) { toast.warning(firstError); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
        const saveError = validateSave(total, lines);
        if (saveError) { toast.warning(saveError); return; }
        if (!window.confirm(`Save receipt for ${fmt3(total)}?`)) return;

        setServerError('');
        setSaving(true);
        const res = await api.createReceipt(buildCreateRequest(payment, lines));
        setSaving(false);
        if (!res.ok) {
            // Keep the grid and the user's input; the message belongs to the payment panel.
            setServerError(res.message);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        const receiptNo = res.result?.receiptNo;
        toast.success(`Successfully saved. Receipt No: ${receiptNo}`);
        setLines([]);
        navigate(MISC_RECEIPT_BASE, { state: { highlightReceiptNo: receiptNo, refresh: Date.now() } });
    };

    const handleCancel = () => {
        if (lines.length > 0 && !window.confirm(LEAVE_MESSAGE)) return;
        navigate(MISC_RECEIPT_BASE);
    };

    if (permissions?.canAdd !== true) return <div className="p-3"><NoAccess /></div>;

    const panelDisabled = !unlocked || saving;

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between border-box2 p-3" style={{ borderRadius: '8px' }}>
                <h6 className="h6">Miscellaneous Receipt</h6>
                <div className="d-flex justify-content-end gap-1">
                    <button type="button" className="mainbtn" disabled={!canSave} onClick={handleSave}>
                        <FaSave className="center mt-1 mx-2" /><div>Save</div>
                    </button>
                    <button type="button" className="mainbtnSecondary" disabled={saving} onClick={handleCancel}>
                        <FaAngleLeft className="center mt-1 mx-2" /><div>Cancel</div>
                    </button>
                </div>
            </div>

            <div className="mt-3">
                <PaymentPanel
                    value={payment}
                    onChange={handlePaymentChange}
                    onTransactionTypeChange={handleTransactionTypeChange}
                    errors={errors}
                    serverError={serverError}
                    unlocked={unlocked}
                    saving={saving}
                    transactionTypes={dropdowns.transactionTypes}
                    debitAccounts={debitAccounts}
                    drawnOnBanks={dropdowns.drawnOnBanks}
                />
            </div>
            <div className="mt-3">
                <AllocationPanel
                    alloc={alloc}
                    details={details}
                    balanceAmount={remaining}
                    disabled={panelDisabled}
                    loading={lookupLoading}
                    warning={warning}
                    properties={dropdowns.properties}
                    units={units}
                    leases={leases}
                    tenants={dropdowns.tenants}
                    paymentTypes={dropdowns.paymentTypes}
                    onProperty={handleProperty}
                    onUnit={handleUnit}
                    onLease={handleLease}
                    onTenant={handleTenant}
                    onPaymentType={handlePaymentType}
                    onAmount={(amount) => setAlloc((a) => ({ ...a, amount }))}
                    onShowAll={handleShowAll}
                    onAdd={handleAdd}
                    onReset={handleReset}
                />
            </div>
            <div className="mt-3">
                <LinesGrid lines={lines} allocated={allocated} onRemove={handleRemove} disabled={panelDisabled} />
            </div>

            {saving && (
                <div
                    role="alert"
                    aria-busy="true"
                    style={{
                        position: 'fixed', inset: 0, zIndex: 20000, background: 'rgba(0,0,0,0.55)', color: '#fff',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
                    }}
                >
                    <ClipLoader color="#ffffff" size={48} />
                    <div>Saving receipt, please wait…</div>
                </div>
            )}
        </div>
    );
};

export default MiscellaneousReceiptRAForm;
